import PlusIcon from '@src/svgs/PlusIcon';
import { useState } from 'react';
import styled from 'styled-components';

import CollectionFormModal from './CollectionFormModal';
import CollectionPicker from './CollectionPicker';
import CollectionRow from './CollectionRow';

import type {
  TranslationMemoryCollection,
  TranslationMemoryCollectionInput,
} from '@src/repositories/TranslationMemoryCollectionRepository';

/**
 * Sentinel id for the "Default (uncategorized)" pseudo-row — entries with no
 * collection (E12/OQ6). It is a UI label, not a real record, so it is never
 * renamed or deleted; it always sits first and is selectable.
 */
export const DEFAULT_COLLECTION_ID = '__default__';

/**
 * Sentinel id for the "All translations" pseudo-row — shows every entry across
 * all collections (and Default). Like Default, it is a UI-only view, never a
 * record; it sits at the very top and is selectable but not editable.
 */
export const ALL_COLLECTIONS_ID = '__all__';

type Status = 'idle' | 'loading' | 'ready' | 'error';

/** Mutation handlers resolve `null` on success or an error message to surface. */
interface Props {
  collections: TranslationMemoryCollection[];
  status: Status;
  error: string | null;
  selectedId: string;
  onSelect: (id: string) => void;
  onCreate: (input: TranslationMemoryCollectionInput) => Promise<string | null>;
  onRename: (
    id: string,
    input: TranslationMemoryCollectionInput,
  ) => Promise<string | null>;
  onDelete: (id: string) => Promise<string | null>;
  retry: () => void;
}

const CollectionList = ({
  collections,
  status,
  error,
  selectedId,
  onSelect,
  onCreate,
  onRename,
  onDelete,
  retry,
}: Props) => {
  const [creating, setCreating] = useState(false);

  return (
    <Panel>
      <PanelHeader>
        <h3>Collections</h3>
        {!creating && (
          <NewButton type="button" onClick={() => setCreating(true)}>
            <PlusIcon
              width="14"
              height="14"
              viewBox="0 0 40 40"
              strokeColor="currentColor"
            />
            New Collection
          </NewButton>
        )}
      </PanelHeader>

      {/* Narrow screens: a compact dropdown instead of the full table (the
          two-pane layout stacks below 900px, where the table wastes vertical
          space). Rename/delete stay on the wide-screen table. */}
      <MobilePicker>
        <CollectionPicker
          selectedId={selectedId}
          onSelect={onSelect}
          options={[
            {
              id: ALL_COLLECTIONS_ID,
              name: 'All translations',
              description: 'Every collection',
            },
            {
              id: DEFAULT_COLLECTION_ID,
              name: 'Default',
              description: 'Uncategorized',
            },
            ...collections.map(c => ({
              id: c.id,
              name: c.name,
              description: c.description ?? '',
            })),
          ]}
        />
      </MobilePicker>

      <DesktopTable>
        <TableScroll>
          <Table>
            <tbody>
              <DefaultRow
                $selected={selectedId === ALL_COLLECTIONS_ID}
                onClick={() => onSelect(ALL_COLLECTIONS_ID)}
              >
                <td>
                  <DefaultCell>
                    <span className="name">All translations</span>
                    <span className="hint">Every collection</span>
                  </DefaultCell>
                </td>
              </DefaultRow>

              <DefaultRow
                $selected={selectedId === DEFAULT_COLLECTION_ID}
                onClick={() => onSelect(DEFAULT_COLLECTION_ID)}
              >
                <td>
                  <DefaultCell>
                    <span className="name">Default</span>
                    <span className="hint">Uncategorized</span>
                  </DefaultCell>
                </td>
              </DefaultRow>

              {collections.map(collection => (
                <CollectionRow
                  key={collection.id}
                  collection={collection}
                  selected={selectedId === collection.id}
                  onSelect={() => onSelect(collection.id)}
                  onRename={input => onRename(collection.id, input)}
                  onDelete={() => onDelete(collection.id)}
                />
              ))}
            </tbody>
          </Table>
        </TableScroll>
      </DesktopTable>

      {status === 'loading' && collections.length === 0 && (
        <StatusText>Loading collections…</StatusText>
      )}
      {status === 'ready' && collections.length === 0 && !creating && (
        <StatusText>
          No collections yet. Everything lands in Default until you add one.
        </StatusText>
      )}
      {status === 'error' && (
        <StatusText role="alert">
          {error}{' '}
          <button type="button" onClick={retry}>
            Retry
          </button>
        </StatusText>
      )}

      {creating && (
        <CollectionFormModal
          mode="create"
          onClose={() => setCreating(false)}
          onSubmit={onCreate}
        />
      )}
    </Panel>
  );
};

export default CollectionList;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  /* Match the entry pane's toolbar height (a label line + gap + the select) so
     the collection list lines up at the top with the entry table. */
  min-height: calc(var(--btn-h-sm) + 20px);

  > h3 {
    font-size: var(--text-base);
    font-weight: var(--font-weight-bold);
    color: var(--foreground);
    margin: 0;
  }
`;

const NewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 6px;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: var(--primary);

  &:hover {
    color: var(--primary-hover);
  }
`;

// The full table is the default; the dropdown replaces it below the layout's
// stacking breakpoint (900px, matching TranslationMemorySection's Layout).
const DesktopTable = styled.div`
  @media (max-width: 900px) {
    display: none;
  }
`;

const MobilePicker = styled.div`
  display: none;

  @media (max-width: 900px) {
    display: block;
  }
`;

const TableScroll = styled.div`
  overflow: auto;
  max-height: 55vh;
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  > tbody tr:last-child {
    border-bottom: none;
  }
`;

const DefaultRow = styled.tr<{ $selected: boolean }>`
  cursor: pointer;
  border-bottom: 1px solid var(--border);
  ${props => props.$selected && `box-shadow: inset 2px 0 0 var(--primary);`}

  &:hover {
    background: var(--surface-elevated);
  }

  > td {
    padding: 10px 12px;
  }
`;

const DefaultCell = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;

  .name {
    font-size: var(--text-sm);
    font-weight: var(--font-weight-medium);
    color: var(--foreground);
  }

  .hint {
    font-size: var(--text-xs);
    color: var(--text-muted);
  }
`;

const StatusText = styled.p`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  margin: 4px 2px;

  > button {
    background: none;
    border: none;
    color: var(--primary);
    cursor: pointer;
    font-size: inherit;
    padding: 0;
  }
`;
